import {
  AfterContentInit,
  Component,
  ContentChildren,
  EventEmitter,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { WorkspacesResource } from '../../../workspaces/resources/workspaces.resource';
import { SearchConfig } from '../../services/assisted-search/search-config.model';
import { TemplateNameDirective } from '../template-name.directive';
import { NotifyService } from '../../services/notify.service';

export interface LoadMoreQuery {
  offset: number;
  limit: number;
}

export enum GenericListType {
  members = 'members',
  hooks = 'hooks',
  packages = 'packages',
  connections = 'connections',
  jobs = 'jobs',
  schedules = 'schedules',
  clusters = 'clusters',
  accounts = 'accounts',
  keys = 'keys',
  workspaces = 'workspaces',
}

@Component({
  selector: 'generic-list',
  template: `
    <div class="generic-list">
      <div class="workspaces-header" *ngIf="type === genericListTypes.packages && isWorkspacesItemView && !isLoading">
        <i class="fa fa-arrow-left"></i>
        <a (click)="goToWorkspaces()"
          ><span class="workspaces-back">{{ 'workspace.generic-list.item.labels.back' | translate }}</span></a
        >
        <span class="workspace-name">{{ workspaceName }}</span>
      </div>

      <div class="generic-list-header">
        <assisted-search
          *ngIf="hasSearch"
          [configHash]="searchConfig"
          (submit)="onSearchSubmit($event)"
          [inputValue]="searchQueryFromUrl"
          [placeholder]="getSearchPlaceholder()"
        >
          <assisted-search-help [type]="type"></assisted-search-help>
        </assisted-search>

        <div class="generic-list-search invisible" *ngIf="!hasSearch"></div>

        <button
          class="generic-list-new-button btn btn-info btn-lg"
          type="button"
          (click)="createClick.emit()"
          [disabled]="newBtnDisabled"
          *ngIf="hasNewButton"
        >
          {{ newBtnText | translate }}
        </button>
      </div>

      <ng-template [ngTemplateOutlet]="listTemplates.headers"></ng-template>

      <div *ngIf="items.length" class="generic-list-body folders {{ type }}">
        <ng-template
          *ngFor="let item of items; trackBy: identify"
          [ngTemplateOutlet]="listTemplates.listItem"
          [ngTemplateOutletContext]="{ $implicit: item }"
        ></ng-template>
      </div>
      <div *ngIf="!items.length && !isLoading && !isError">
        <generic-list-empty
          [type]="type"
          [emptyDescription]="emptyDescription"
          [emptyTitle]="emptyTitle"
          [lastAction]="lastAction"
          [search]="search"
        ></generic-list-empty>
      </div>
      <div *ngIf="isError" class="generic-list-error">
        An error occurred while loading data. <br />Please try to refresh the page. <br />If the problem still persists,
        contact our support team.
      </div>
      <div class="generic-list-loader" *ngIf="isLoading">
        <xp-loader></xp-loader>
      </div>
    </div>
  `,
})
export class GenericListComponent implements OnInit, OnDestroy, AfterContentInit {
  @Input() type: GenericListType;
  @Input() items: any[] = [];
  @Input() isLoading = false;
  @Input() hasNewButton = false;
  @Input() hasSearch = false;
  @Input() newBtnText = '';
  @Input() emptyDescription: string;
  @Input() emptyTitle: string;
  @Input() searchPlaceholder: string;
  @Input() searchConfig: SearchConfig;
  @Input() searchQueryFromUrl: any;
  @Input() limit = 10;
  @Input() allItemsLoaded = false;
  @Input() newBtnDisabled = false;
  @Input() isError = false;
  @Output() createClick = new EventEmitter();
  @Output() loadMore = new EventEmitter();
  @Output() searchSubmit = new EventEmitter();
  @ContentChildren(TemplateNameDirective) templates: QueryList<TemplateNameDirective>;

  offset = 0;
  isWorkspacesItemView = false;
  workspaceName = '';
  lastAction: 'search' | 'list' = 'list';
  search: 'search' | 'list' = 'list';
  listTemplates = {};
  genericListTypes = GenericListType;

  constructor(
    @Inject('$rootScope') private $rootScope: any,
    private translate: TranslateService,
    private workspacesResource: WorkspacesResource,
    private notify: NotifyService,
    @Inject('$stateParams') private $stateParams: any,
  ) {}

  ngAfterContentInit() {
    const templatesArray = this.templates.toArray();
    this.listTemplates = templatesArray.reduce((acc, curr) => ({ ...acc, [curr.templateName]: curr.template }), {});
  }

  ngOnInit() {
    this.isWorkspacesItemView = this.$rootScope.stateIs('app.account.workspaces-item');

    if (this.isWorkspacesItemView) {
      this.workspacesResource.get(this.$stateParams.workspace_id).subscribe({
        next: (item) => {
          this.workspaceName = item.name;
        },
        error: () => {
          this.notify.error('An error occurred while loading workspace item.');
        },
      });
    }

    const scrollWrapper = document.querySelector('.page-scroll-wrapper');

    if (scrollWrapper) {
      scrollWrapper.addEventListener('ps-y-reach-end', this.scrollReachEnd.bind(this));
    }
  }

  ngOnDestroy() {
    const scrollWrapper = document.querySelector('.page-scroll-wrapper');

    if (scrollWrapper) {
      scrollWrapper.removeEventListener('ps-y-reach-end', this.scrollReachEnd.bind(this));
    }
  }

  goToWorkspaces() {
    this.$rootScope.stateGo('app.account.workspaces');
  }

  onSearchSubmit({ query }) {
    this.lastAction = query ? 'search' : 'list';
    this.search = query;

    this.searchSubmit.emit(query);
  }

  // eslint-disable-next-line class-methods-use-this
  identify(index: number, item: any) {
    return item.id;
  }

  scrollReachEnd(event: any) {
    if (this.isLoading || this.allItemsLoaded || event.target.scrollTop === 0) {
      return;
    }

    this.offset += this.limit;
    this.loadMore.emit({ offset: this.offset, limit: this.limit });
  }

  getSearchPlaceholder() {
    if (!this.workspaceName && this.type === 'packages' && this.isWorkspacesItemView) {
      return '';
    }

    return this.type === 'packages' && this.isWorkspacesItemView
      ? this.translate.instant('package.generic-object.placeholders.search-workspaces', {
          workspace: this.workspaceName,
        })
      : this.searchPlaceholder || this.translate.instant('generic-list.defaults.search_placeholder');
  }
}
